/**
 * Orchestrates label printing for in-progress consultation treatments.
 *
 * Batch suggestions are computed client-side via FEFO allocation against the
 * stock data included in the consultation detail response. They are optimistic
 * (not committed) since the order hasn't been prepared yet - the actual batch
 * assignment happens at order preparation time.
 */
import { useCallback, useMemo } from 'react'

import type { ConsultationWithStock, WithNonNullable } from '@snootsvet/shared'
import {
  formatCalendarDate,
  formatLatestWeight,
  formatName,
  getClinics,
  resolveDateFormat,
} from '@snootsvet/shared'

import { treatmentToLabelData } from '@/frontend/features/printing/label-printing.utils'
import { usePrintLabels } from '@/frontend/features/printing/labels/use-print-labels'
import { useViewerRegion } from '@/frontend/features/region/region-context'
import { useGetPatient } from '@/frontend/hooks/api/patients/use-get-patient'
import { useListPatientVitals } from '@/frontend/hooks/api/patients/use-list-patient-vitals'
import { computeStockAllocation } from '@/frontend/lib/stock'

export function usePrintOptimisticLabels(consultation: ConsultationWithStock) {
  const { data: patient } = useGetPatient(consultation.patientId, (r) => r.patient)
  const { data: vitals } = useListPatientVitals(consultation.patientId)
  const { region } = useViewerRegion()
  const clinics = getClinics({ includeArchived: true })
  const { print, isPrinting } = usePrintLabels()

  const printableTreatments = useMemo(
    () =>
      (consultation.treatments ?? []).filter(
        (t): t is WithNonNullable<ConsultationWithStock['treatments'][number], 'product'> =>
          !t.isExternal &&
          !t.administeredInConsult &&
          t.quantity > 0 &&
          !!t.productId &&
          t.product !== null
      ),
    [consultation.treatments]
  )

  const hasPrintableTreatments = printableTreatments.length > 0
  const canPrint = !!patient && !!clinics && hasPrintableTreatments

  const printLabels = useCallback(async () => {
    if (!patient || !clinics) return

    const defaultClinicId = consultation.appointment?.clinicId ?? null
    const ownerName = formatName(patient.owner)
    const weight = formatLatestWeight(vitals, region)

    const labels = printableTreatments.map((t) => {
      const clinicId = t.orders[0]?.clinicId ?? defaultClinicId

      // The label is generated for the owner, so it follows the dispensing
      // clinic's zone and the owner's region rather than the printing vet's.
      const fmt = resolveDateFormat(clinicId, patient.owner.region)

      const batches = (() => {
        if (!clinicId) return []
        const clinicStock = t.product.stock.filter((s) => s.clinicId === clinicId)
        const { allocations } = computeStockAllocation(clinicStock, t.quantity)
        return allocations
          .filter((a) => a.batchNumber)
          .map((a) => ({
            batchNumber: a.batchNumber!,
            // `stock.expiryDate` is a `date` column — it names a day, so no zone applies.
            expiryDate: a.expiryDate ? formatCalendarDate(a.expiryDate, fmt) : null,
          }))
      })()

      return treatmentToLabelData({
        treatment: t,
        clinic: clinics.find((c) => c.id === clinicId),
        patient: { name: patient.name, species: patient.species, breed: patient.breed },
        ownerName,
        weight,
        prescriber: consultation.staff,
        batches,
        fmt,
      })
    })

    await print(labels, `labels-consultation-${consultation.id}.pdf`)
  }, [patient, vitals, region, clinics, consultation, printableTreatments, print])

  return { printLabels, canPrint, hasPrintableTreatments, isPrinting }
}
